// biome-ignore-all lint/correctness/useHookAtTopLevel: AUTH_PROVIDER is a compile-time constant, so the provider-switch in useAuth() only ever runs one branch and hook order is stable per build (mirrors the prior eslint-disable rules-of-hooks)
import { useAuth0 } from "@auth0/auth0-react";
import { useAuth as useClerkAuth } from "@clerk/clerk-react";
import { useCallback } from "react";
import { AUTH_PROVIDER } from "./config";

interface AuthState {
  isLoading: boolean;
  hasError: boolean;
  isAuthenticated: boolean;
  getToken: () => Promise<string | null>;
  login: () => void;
  logout: () => void;
}

/**
 * Provider-agnostic auth hook
 * Returns the same interface regardless of Auth0 or Clerk
 */
export function useAuth(): AuthState {
  if (AUTH_PROVIDER === "clerk") {
    return useClerkAuthState();
  }
  return useAuth0State();
}

function useClerkAuthState(): AuthState {
  const {
    isLoaded,
    isSignedIn,
    signOut,
    getToken: clerkGetToken,
  } = useClerkAuth();

  const getToken = useCallback(async () => {
    try {
      return await clerkGetToken();
    } catch {
      return null;
    }
  }, [clerkGetToken]);

  const login = useCallback(() => {
    window.location.href = "/sign-in";
  }, []);

  const logout = useCallback(() => {
    void signOut({ redirectUrl: "/" });
  }, [signOut]);

  return {
    isLoading: !isLoaded,
    hasError: false,
    isAuthenticated: !!isSignedIn,
    getToken,
    login,
    logout,
  };
}

function useAuth0State(): AuthState {
  const {
    isLoading,
    error,
    isAuthenticated,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
  } = useAuth0();

  const getToken = useCallback(async () => {
    try {
      return await getAccessTokenSilently();
    } catch {
      return null;
    }
  }, [getAccessTokenSilently]);

  const login = useCallback(() => {
    void loginWithRedirect();
  }, [loginWithRedirect]);

  const handleLogout = useCallback(() => {
    void logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  return {
    isLoading,
    hasError: !!error,
    isAuthenticated,
    getToken,
    login,
    logout: handleLogout,
  };
}
